import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  documentOwnerLabels,
  documents,
  type DocumentRecord,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { KpiCard } from '@/shared/ui/KpiCard';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { classeDaAba, trilhoDeAbas } from '@/shared/ui/tabStyles';

import { PartyToolbar } from '../shared/PartyToolbar';
import { DocumentSettingsTab } from './DocumentSettingsTab';
import { DocumentTypesTab } from './DocumentTypesTab';
import { InsurerDocumentsTab } from './InsurerDocumentsTab';

/**
 * Tela Documentos.
 *
 * A primeira aba e a tela /documentos do OnPoint: os documentos da seguradora, que a
 * corretora tambem le. As outras sao do SeguraCore: as exigencias de todos os donos
 * (o repositorio central), o catalogo de tipos e a configuracao do repositorio. A
 * corretora ve so a primeira, porque a leitura geral do repositorio nao vale com
 * escopo de corretora.
 */

type Aba = 'seguradora' | 'exigencias' | 'tipos' | 'configuracao';

export function DocumentsPage() {
  const { can } = useAuth();
  const geral = can('documentos.documento.view');
  const daSeguradora = geral || can('documentos.seguradora.view');

  const abas: { chave: Aba; rotulo: string }[] = [
    ...(daSeguradora
      ? [{ chave: 'seguradora' as const, rotulo: 'Documentos da seguradora' }]
      : []),
    ...(geral
      ? [
          { chave: 'exigencias' as const, rotulo: 'Exigencias' },
          { chave: 'tipos' as const, rotulo: 'Tipos de documento' },
          { chave: 'configuracao' as const, rotulo: 'Configuracao' },
        ]
      : []),
  ];
  const [escolhida, setEscolhida] = useState<Aba>('seguradora');
  const aba = abas.some((item) => item.chave === escolhida)
    ? escolhida
    : (abas[0]?.chave ?? 'seguradora');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Documentos"
        description="Documentos da seguradora e repositorio central, com exigencia, versao e trilha."
      />

      {abas.length > 1 ? (
        <div role="tablist" aria-label="Documentos" className={trilhoDeAbas}>
          {abas.map((item) => (
            <button
              key={item.chave}
              type="button"
              role="tab"
              id={`aba-${item.chave}`}
              aria-selected={aba === item.chave}
              aria-controls={`painel-${item.chave}`}
              onClick={() => setEscolhida(item.chave)}
              className={
                aba === item.chave ? classeDaAba(true, 'xs') : classeDaAba(false, 'xs')
              }
            >
              {item.rotulo}
            </button>
          ))}
        </div>
      ) : null}

      <div role="tabpanel" id={`painel-${aba}`} aria-labelledby={`aba-${aba}`}>
        {aba === 'seguradora' && daSeguradora ? <InsurerDocumentsTab /> : null}
        {aba === 'exigencias' ? <RequirementsTab /> : null}
        {aba === 'tipos' ? <DocumentTypesTab /> : null}
        {aba === 'configuracao' ? <DocumentSettingsTab /> : null}
      </div>
    </div>
  );
}

// --- Exigencias ----------------------------------------------------------------

const limite = 25;

const vinculos = [
  { value: '', label: 'Todos os vinculos' },
  ...Object.entries(documentOwnerLabels).map(([value, label]) => ({ value, label })),
];

const situacoes = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'recebido', label: 'Recebido' },
  { value: 'dispensado', label: 'Dispensado' },
];

const rotuloDaSituacao: Record<
  string,
  { label: string; tone: 'warning' | 'success' | 'neutral' }
> = {
  pendente: { label: 'Pendente', tone: 'warning' },
  recebido: { label: 'Recebido', tone: 'success' },
  dispensado: { label: 'Dispensado', tone: 'neutral' },
};

/**
 * Aba Exigencias: tudo o que a empresa exige e recebe, de qualquer vinculo.
 *
 * O envio acontece onde o documento vive: na operacao, na apolice, no tomador. Aqui a
 * pergunta e sobre o conjunto: o que falta chegar. Os cartoes seguem os filtros da
 * lista, menos o da situacao.
 */
function RequirementsTab() {
  const [busca, setBusca] = useState('');
  const [vinculo, setVinculo] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);

  const consulta = useQuery({
    queryKey: ['documents', { busca, vinculo, status, offset }],
    queryFn: ({ signal }) =>
      documents.list(
        { q: busca, owner_type: vinculo, status, limit: limite, offset },
        signal,
      ),
  });

  const armazenamento = useQuery({
    queryKey: ['document-storage'],
    queryFn: ({ signal }) => documents.storage(signal),
    staleTime: 5 * 60 * 1000,
  });

  const contagem = consulta.data?.byStatus ?? {};

  const colunas: Column<DocumentRecord>[] = [
    {
      key: 'titulo',
      header: 'Documento',
      render: (document) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">{document.title}</p>
          <p className="truncate text-2xs text-ink-500">
            {document.group || 'Sem grupo'}
          </p>
        </div>
      ),
    },
    {
      key: 'vinculo',
      header: 'Vinculo',
      hideBelow: 'md',
      render: (document) =>
        documentOwnerLabels[document.owner_type] ?? document.owner_type,
    },
    {
      key: 'exigencia',
      header: 'Exigencia',
      hideBelow: 'lg',
      render: (document) =>
        document.required ? (
          <Badge tone="neutral">Obrigatorio</Badge>
        ) : (
          <span className="text-2xs text-ink-500">Opcional</span>
        ),
    },
    {
      key: 'versao',
      header: 'Versao',
      align: 'right',
      render: (document) =>
        document.current_version > 0 ? (
          <span className="tabular-nums">v{document.current_version}</span>
        ) : (
          <span className="text-2xs text-ink-400">--</span>
        ),
    },
    {
      key: 'insercao',
      header: 'Data de insercao',
      align: 'right',
      hideBelow: 'lg',
      render: (document) => formatDate(document.created_at),
    },
    {
      key: 'atualizado',
      header: 'Atualizado',
      align: 'right',
      hideBelow: 'lg',
      render: (document) => formatDate(document.updated_at),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (document) => {
        const situacao = rotuloDaSituacao[document.status] ?? {
          label: document.status,
          tone: 'neutral' as const,
        };
        return (
          <div className="flex flex-wrap items-center gap-1">
            <Badge tone={situacao.tone}>{situacao.label}</Badge>
            {document.expired ? <Badge tone="danger">Vencido</Badge> : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os documentos">
          {consulta.error.message}
        </Alert>
      ) : null}

      {armazenamento.data && !armazenamento.data.available ? (
        <Alert tone="info" title="Envio de arquivo indisponivel nesta instalacao">
          {armazenamento.data.missing_requirement} Ate la, os documentos continuam sendo
          exigidos, versionados e auditados normalmente.
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        <KpiCard
          label="Pendentes"
          value={String(contagem.pendente ?? 0)}
          icon="clock"
          tone="warning"
          highlight={(contagem.pendente ?? 0) > 0}
          loading={consulta.isPending}
        />
        <KpiCard
          label="Recebidos"
          value={String(contagem.recebido ?? 0)}
          icon="file"
          tone="success"
          loading={consulta.isPending}
        />
        <KpiCard
          label="Dispensados"
          value={String(contagem.dispensado ?? 0)}
          icon="close"
          tone="indigo"
          loading={consulta.isPending}
        />
      </div>

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por titulo ou grupo"
          filter={
            <Select
              label="Vinculo"
              className="h-9"
              options={vinculos}
              value={vinculo}
              onChange={(event) => {
                setVinculo(event.target.value);
                setOffset(0);
              }}
            />
          }
          extra={
            <Select
              label="Situacao"
              className="h-9"
              options={situacoes}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setOffset(0);
              }}
            />
          }
        />

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(document) => document.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="file"
              title={
                busca || vinculo || status
                  ? 'Nenhum documento encontrado'
                  : 'Nenhum documento exigido'
              }
              description="O documento nasce onde ele vive: na operacao, na apolice, no tomador. Exija por la e ele aparece aqui."
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>
    </div>
  );
}
